/**
 * HeroStateCard — Parent Home hero (iteration 18 redesign).
 *
 * Layout (top to bottom, mobile-first):
 *   • Small circular portrait + name + classroom group
 *   • Big PASTEL state card with the current state phrase and a soft lucide icon
 *   • "Updated 2 min ago by Teacher Maria" + a calm "Up to date" / "Needs a fresh note" pill
 */
import { Link } from "react-router-dom";
import {
  Trees,
  Sparkles,
  UtensilsCrossed,
  BedDouble,
  Heart,
  LogIn,
  Home as HomeIcon,
} from "lucide-react";

const STATE_LABEL = {
  safe: "In Care",
  arrived: "Just Arrived",
  learning: "In the Yard",
  playing: "At Play",
  eating: "Sharing a Meal",
  resting: "Resting",
  with_family: "With Family",
  pickup_arriving: "Pickup arriving",
  away: "Resting at home",
};

const STATE_COLOR = {
  safe: "var(--pastel-care)",
  arrived: "var(--pastel-care)",
  learning: "var(--pastel-yard)",
  playing: "var(--pastel-play)",
  eating: "var(--pastel-eat)",
  resting: "var(--pastel-rest)",
  with_family: "var(--pastel-care)",
  pickup_arriving: "var(--pastel-care)",
  away: "var(--pastel-rest)",
};

const STATE_ICON = {
  safe: Heart,
  arrived: LogIn,
  learning: Trees,
  playing: Sparkles,
  eating: UtensilsCrossed,
  resting: BedDouble,
  with_family: HomeIcon,
  pickup_arriving: Heart,
  away: BedDouble,
};

function timeSince(iso) {
  if (!iso) return null;
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.round(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m} min ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  return new Date(iso).toLocaleDateString();
}

export default function HeroStateCard({ child, reassurance }) {
  if (!child) return null;
  const state = child.current_state || "safe";
  const pastel = STATE_COLOR[state] || "var(--pastel-care)";
  const phrase = STATE_LABEL[state] || "In Care";
  const Icon = STATE_ICON[state] || Heart;
  const updated = timeSince(child.state_updated_at);
  // Iteration 21 — 4-bucket confidence (10/25 min thresholds) takes priority,
  // gracefully falling back to legacy 2h/8h field if the backend hasn't been
  // upgraded yet.
  const conf = child.status_confidence || child.confidence || "needs_confirmation";
  const CONF_META = {
    fresh: { label: "Up to date", color: "#34C759" },
    aging: { label: "Aging", color: "#FFB800" },
    needs_confirmation: { label: "Needs a fresh note", color: "#9CA3AF" },
    stale: { label: "Needs a fresh note", color: "#9CA3AF" },
    not_applicable: { label: "Resting at home", color: "#1F3A2A" },
  };
  const confMeta = CONF_META[conf] || CONF_META.needs_confirmation;

  return (
    <section
      data-testid="home-hero"
      data-state={state}
      className="mt-6"
    >
      {/* Avatar row */}
      <div className="flex items-center gap-4">
        <div
          data-testid="home-avatar"
          className="h-14 w-14 rounded-full overflow-hidden bg-[#F7F8FC] shrink-0 ring-2 ring-[#34C759]/60"
        >
          {child.portrait_url ? (
            <img
              src={child.portrait_url}
              alt={child.name}
              className="h-full w-full object-cover"
            />
          ) : (
            <div className="h-full w-full flex items-center justify-center text-xl font-semibold text-[#1F3A2A]">
              {child.name[0]}
            </div>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-display text-2xl text-[#1A1B2E] tracking-tight truncate leading-tight">
            {child.name}
          </div>
          {child.group_name && (
            <div className="text-xs text-[#6B7280] font-medium tracking-wide mt-0.5">
              {child.group_name}
            </div>
          )}
        </div>
      </div>

      {/* Big pastel state card */}
      <Link
        to="/messages"
        data-testid="home-state-card"
        className="mt-5 block rounded-3xl px-6 py-8 sm:px-8 sm:py-10 transition-transform duration-200 hover:-translate-y-0.5 relative overflow-hidden shadow-[0_2px_8px_rgba(16,24,40,0.04)]"
        style={{ background: pastel }}
        aria-label={`${child.name} is ${phrase}`}
      >
        {/* Soft icon halo bottom-right */}
        <div
          aria-hidden
          className="absolute -right-6 -bottom-6 h-44 w-44 rounded-full flex items-end justify-end p-7 opacity-25"
          style={{ background: "rgba(26, 27, 46, 0.06)" }}
        >
          <Icon size={92} strokeWidth={1.2} className="text-[#1A1B2E]" />
        </div>
        <div className="relative">
          <div className="eyebrow text-[#1A1B2E]/55">Right now</div>
          <h1
            data-testid="home-state-phrase"
            className="mt-3 font-display text-5xl sm:text-6xl leading-[1.02] tracking-tight text-[#1A1B2E]"
          >
            {phrase}
          </h1>
          {reassurance?.message && (
            <p
              data-testid="home-reassurance"
              className="mt-4 font-display italic font-light text-xl text-[#1A1B2E]/75 leading-snug max-w-md"
            >
              {reassurance.message}
            </p>
          )}
        </div>
      </Link>

      {/* Calm under-line */}
      <div className="mt-4 flex items-center justify-between flex-wrap gap-2 text-sm">
        <span
          data-testid="home-updated-by"
          className="text-[#6B7280] font-medium"
        >
          {updated
            ? `Updated ${updated}${
                child.state_updated_by_name
                  ? ` by ${child.state_updated_by_name}`
                  : ""
              }`
            : "No update yet"}
        </span>
        <span
          data-testid={`home-freshness-${conf}`}
          className="inline-flex items-center gap-2 text-[11px] tracking-[0.04em] uppercase text-[#6B7280] font-semibold"
        >
          <span
            aria-hidden
            className="h-2 w-2 rounded-full"
            style={{ background: confMeta.color }}
          />
          {confMeta.label}
        </span>
      </div>
    </section>
  );
}
